import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  ReactElement,
  useMemo,
  useRef,
} from 'react';

import { getProvider } from '@provider-directory/app/actions/get-provider';
import {
  GroupedCachedSlotsByLocation,
  groupCachedSlotsByLocation,
  getNearestAvailability,
  type NearestAvailability,
} from '@provider-directory/components/availability/availability.model';
import { useFeatureFlags } from '@provider-directory/contexts/FlagContext';

import type { ClientOrgContextInitials } from '@provider-directory/types/organizations.contracts';
import { ProviderResource } from '@provider-directory/types/providers.contracts';

interface CachedSlotsContextValue {
  isLoading: boolean;
  cachedSlots?: GroupedCachedSlotsByLocation | null;
  refetchProviderWithRFV: (reasonForVisit: string) => Promise<void>;
  nearestAvailability: NearestAvailability | null;
}

const DEFAULT_CACHED_SLOTS_CONTEXT: CachedSlotsContextValue = {
  isLoading: true,
  cachedSlots: undefined,
  refetchProviderWithRFV: async () => Promise.resolve(),
  nearestAvailability: null,
};

const CachedSlotsContext = createContext<CachedSlotsContextValue>(
  DEFAULT_CACHED_SLOTS_CONTEXT,
);

interface CachedSlotsProviderProps {
  children: ReactNode;
  provider: ProviderResource | null;
  skipPrevSlots?: boolean;
  hospitalAssociations?: {
    code: ClientOrgContextInitials;
    name: string;
  }[];
}

export const CachedSlotsProvider = ({
  children,
  provider,
}: CachedSlotsProviderProps): ReactElement => {
  const activeRFVRef = useRef<string | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [cachedSlots, setCachedSlots] =
    useState<GroupedCachedSlotsByLocation | null>();

  const {
    featureFlags: { isSchedulingEnabled },
  } = useFeatureFlags();

  useEffect(() => {
    if (!isSchedulingEnabled) {
      setIsLoading(false);
      return;
    }

    if (provider?.schedule && activeRFVRef.current === null) {
      const groupedCachedSlots = groupCachedSlotsByLocation(provider.schedule);
      setCachedSlots(groupedCachedSlots);
      setIsLoading(false);
    }
  }, [isSchedulingEnabled, provider?.schedule]);

  const refetchProviderWithRFV = useCallback(
    async (reasonForVisit: string): Promise<void> => {
      if (!provider?.npi || !isSchedulingEnabled) {
        return;
      }

      try {
        setIsLoading(true);
        activeRFVRef.current = reasonForVisit;
        const updatedProvider = await getProvider(provider.npi, reasonForVisit);

        if (updatedProvider?.schedule) {
          const groupedCachedSlots = groupCachedSlotsByLocation(
            updatedProvider.schedule,
          );
          setCachedSlots(groupedCachedSlots);
        } else {
          setCachedSlots(null);
        }
      } catch (error) {
        console.warn('Error refetching provider with RFV:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [provider?.npi, isSchedulingEnabled],
  );

  const values = useMemo(
    (): CachedSlotsContextValue => ({
      isLoading,
      cachedSlots,
      refetchProviderWithRFV,
      nearestAvailability: cachedSlots
        ? getNearestAvailability(cachedSlots)
        : null,
    }),
    [isLoading, cachedSlots, refetchProviderWithRFV],
  );

  return (
    <CachedSlotsContext.Provider value={values}>
      {children}
    </CachedSlotsContext.Provider>
  );
};

export const useCachedSlots = (): CachedSlotsContextValue => {
  const context = useContext(CachedSlotsContext);
  if (!context) {
    throw new Error('useCachedSlots must be used within a CachedSlotsProvider');
  }
  return context;
};
